/* eslint-disable consistent-return */
import { check } from 'meteor/check';
import {
  assign,
  filter,
  find,
  get,
  isEmpty,
  isNil,
  join,
  map,
  snakeCase,
  toNumber,
  isString
} from 'lodash';
import { Meteor } from 'meteor/meteor';
import { HTTP } from 'meteor/http';
import uuid from 'uuid/v1';

import * as connectorOps from '../../Connectors/connector-operations';
import Connectors, { CONNECTOR_SUBTYPES, CONNECTOR_TYPES } from '../../Connectors/Connectors';
import * as sourceOps from '../source-operations';
import Sources from '../Sources';

import { isHTTPResultOK } from '../../../modules/fetch-utilites';
import checkAuthentication from '../../../modules/server/check-authentication';
import handleMethodException from '../../../modules/handle-method-exception';

export const editSourceSchema = Sources.schema.omit('owner', 'createdAt', 'updatedAt', 'deleted');
editSourceSchema.extend({ _id: String });

const {
  checkSourceAPI: CHECK_SOURCE_API,
  infoWrapperAPI: INFO_WRAPPER_API,
  createSourceAPI: CREATE_SOURCE_API,
  deleteSourceAPI: DELETE_SOURCE_API,
  listSourceAttributesAPI: LIST_SOURCE_ATTRIBUTES_API,
  previewSourceAPI: PREVIEW_SOURCE_API,
  checkUrlsSourceAPI: CHECK_URLS_SOURCE_API
} = Meteor.settings.private;

const DUPLICATE_SOURCE = 'Duplicate Source:  "{}" already exists';
const EXISTING_WRAPPER_MESSAGE = 'File or datasource suported';
const FILE_TOO_LARGE_ERROR = 'ERROR 1: Range downloading not supported by this server!';
const STATUS_ERROR = 'Error';
const STATUS_OK = 'ok';
/**
 * Transforms a source into post parameters format.
 *
 * @param {object} source - The source to transform into post parameters.
 * @returns {object} The parameters por the post.
 */
export const getLoadParametersFromSource = source => {
  const connector = Connectors.findOne(sourceOps.getConnectorId(source));
  let portvalue = toNumber(connectorOps.getPort(connector));
  if (portvalue === 0) portvalue = '';
  const loadParameters = {
    protocol: connectorOps.getType(connector),
    type: connectorOps.getSubType(connector),
    domain: connectorOps.getDomain(connector),
    ip: connectorOps.getIp(connector),
    port: portvalue
  };
  switch (true) {
    case connectorOps.isFTP(connector):
      assign(loadParameters, {
        user: connectorOps.getUser(connector),
        password: connectorOps.getPassword(connector),
        source: connectorOps.getFilePath(connector),
        driver_type: connectorOps.getFileType(connector),
        layer_name: sourceOps.getLayerNameSource(source)
      });
      break;
    case connectorOps.isHTTP(connector):
      assign(loadParameters, {
        source: connectorOps.getFilePath(connector),
        driver_type: connectorOps.getFileType(connector),
        user: '',
        password: '',
        layer_name: sourceOps.getLayerNameSource(source)
      });
      break;
    case connectorOps.isPostgreSQL(connector):
      assign(loadParameters, {
        user: connectorOps.getUser(connector),
        password: connectorOps.getPassword(connector),
        database_name: connectorOps.getDatabaseName(connector),
        schema: connectorOps.getSchema(connector),
        driver_type: CONNECTOR_TYPES.POSTGRESQL,
        table_view_name: sourceOps.getLayerNameSource(source)
      });
      break;
    default:
      break;
  }
  return loadParameters;
};

export const getLoadParametersFromWrapper = () => {
  const response = HTTP.post(INFO_WRAPPER_API, {
    headers: { 'Content-Type': 'application/json' },
    data: { user: 'TEST' }
  });
  const result = isHTTPResultOK(response);
  const loadParameters = get(result, 'message', []);

  const CHECK_WRAPPER = {
    protocol: CONNECTOR_TYPES.POSTGRESQL,
    type: CONNECTOR_SUBTYPES.VECTOR,
    domain: '',
    ip: loadParameters[2],
    port: loadParameters[3],
    user: loadParameters[0],
    password: loadParameters[1],
    database_name: loadParameters[4],
    schema: Meteor.userId(),
    driver_type: CONNECTOR_TYPES.POSTGRESQL
  };

  return CHECK_WRAPPER;
};

/**
 * Checks if the source has an existing wrapper.
 *
 * @async
 * @param {object} source - The source to check the wrapper.
 * @returns {object} The status of the source.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const checkIfExistingWrapper = async source => {
  const CHECK_WRAPPER = getLoadParametersFromWrapper(source);
  try {
    const loadParameters = {
      ...CHECK_WRAPPER,
      table_view_name: `${snakeCase(sourceOps.getName(source)) || uuid()}_${sourceOps.getId(
        source
      )}`
    };
    const response = HTTP.post(CHECK_SOURCE_API, {
      headers: { 'Content-Type': 'application/json' },
      data: { input_layer: { ...loadParameters }, user: 'TEST' }
    });
    const result = isHTTPResultOK(response);
    return result.status === STATUS_OK && result.message === EXISTING_WRAPPER_MESSAGE;
  } catch (error) {
    handleMethodException(error);
  }
};

/**
 * Inserts the source in the database.
 *
 * @async
 * @param {object} sourceObject - The source object.
 * @param {object} wrapperOptions - The arguments object.
 * @param {boolean} wrapperOptions.generate - Flag to create a view.
 * @param {string} userId - The user id.
 * @returns {string} Return the id of the new source.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const insert = async (sourceObject, { generate }, userId) => {
  check(userId, String);
  try {
    checkAuthentication(userId);
    Sources.schema.validate(sourceObject, {
      check,
      keys: ['name', 'connectorId', 'layerName', 'attributes']
    });
    const sourceId = Sources.insert({ owner: userId, ...sourceObject });

    if (generate) {
      const loadParameters = getLoadParametersFromSource(sourceObject);
      const response = HTTP.post(CREATE_SOURCE_API, {
        headers: { 'Content-Type': 'application/json' },
        data: {
          input_layer: { ...loadParameters },
          user: Meteor.userId(),
          params: {
            view: `${snakeCase(sourceOps.getName(sourceObject))}_${sourceId}`,
            select: join(sourceOps.getAttributes(sourceObject)),
            where: '',
            groupBy: '',
            having: '',
            orderBy: '',
            limit: ''
          },
          overwrite: false // true o false, da igual
        }
      });
      const result = isHTTPResultOK(response);
      if (result.status === STATUS_ERROR) {
        Sources.remove(sourceId);
        throw new Error(result.message);
      }
    }

    return sourceId;
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Updates the source in the database.
 *
 * @param {object} sourceObject - The source object.
 * @param {object} wrapperOptions - The arguments object.
 * @param {boolean} wrapperOptions.generate - Flag to create a view.
 * @param {boolean} wrapperOptions.overwrite - The overwriteWrapper option.
 * @returns {string} Return the id of the updated source.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const update = (sourceObject, { overwrite }) => {
  const CHECK_WRAPPER = getLoadParametersFromWrapper(sourceObject);
  try {
    checkAuthentication();
    editSourceSchema.validate(sourceObject);
    const sourceId = sourceOps.getId(sourceObject);
    const connector = Connectors.findOne(sourceOps.getConnectorId(sourceObject));
    const oldSource = Sources.findOne(sourceId);
    if (
      connectorOps.isPostgreSQL(connector) ||
      connectorOps.isFTP(connector) ||
      connectorOps.isHTTP(connector)
    ) {
      const oldLoadParameters = {
        ...CHECK_WRAPPER,
        table_view_name: `${snakeCase(sourceOps.getName(oldSource)) || uuid()}_${sourceId}`
      };
      const response = HTTP.post(DELETE_SOURCE_API, {
        headers: { 'Content-Type': 'application/json' },
        data: { input_layer: { ...oldLoadParameters }, user: Meteor.userId() }
      });
      const result = isHTTPResultOK(response);
      if (result.status === STATUS_ERROR) throw new Error(response.message);
    }

    if (overwrite) {
      const loadParameters = getLoadParametersFromSource(sourceObject);
      const response = HTTP.post(CREATE_SOURCE_API, {
        headers: { 'Content-Type': 'application/json' },
        data: {
          input_layer: { ...loadParameters },
          user: Meteor.userId(),
          params: {
            view: `${snakeCase(sourceOps.getName(sourceObject))}_${sourceId}`,
            select: join(sourceOps.getAttributes(sourceObject)),
            where: '',
            groupBy: '',
            having: '',
            orderBy: '',
            limit: ''
          },
          overwrite
        }
      });
      const result = isHTTPResultOK(response);
      if (result.status === STATUS_ERROR) throw new Error(result.message);
    }

    Sources.update(sourceId, { $set: sourceObject });
    return sourceId;
    // Return _id so we can redirect to source after update.
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Copies the source in the database.
 *
 * @param {string} sourceId - The source id.
 * @param {string} userId - The user id that wants the source.
 * @returns {string} Return the id of the new source copied.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const copy = (sourceId, userId) => {
  check(sourceId, String);
  check(userId, String);
  try {
    checkAuthentication();
    const copySourceData = Sources.findOne({ _id: sourceId });
    delete copySourceData.owner;
    delete copySourceData._id;
    delete copySourceData.createdAt;
    delete copySourceData.updatedAt;
    delete copySourceData.deleted;
    copySourceData.name += ' - copy';

    const copySourceId = Sources.insert({ owner: userId, ...copySourceData });

    if (checkIfExistingWrapper(copySourceData)) {
      const loadParameters = getLoadParametersFromSource(copySourceData);
      const response = HTTP.post(CREATE_SOURCE_API, {
        headers: { 'Content-Type': 'application/json' },
        data: {
          input_layer: { ...loadParameters },
          user: Meteor.userId(),
          params: {
            view: `${snakeCase(sourceOps.getName(copySourceData))}_${copySourceId}`,
            select: join(sourceOps.getAttributes(copySourceData)),
            where: '',
            groupBy: '',
            having: '',
            orderBy: '',
            limit: ''
          },
          overwrite: false // true o false, da igual
        }
      });
      const result = isHTTPResultOK(response);
      if (result.status === STATUS_ERROR && result.message !== DUPLICATE_SOURCE) {
        Sources.remove(copySourceId);
        throw new Error(result.message);
      }
    }

    return copySourceId;
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Soft deletes the source in the database.
 *
 * @param {string} sourceId - The source id.
 * @returns {string} Return the id of the source.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const softDelete = sourceId => {
  check(sourceId, String);
  try {
    checkAuthentication();
    return Sources.update(sourceId, { $set: { deleted: new Date().toISOString() } });
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Restores the source in the database.
 *
 * @param {string} sourceId - The source id.
 * @returns {string} Return the id of the source.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const restore = sourceId => {
  check(sourceId, String);
  try {
    checkAuthentication();
    const source = Sources.findOne(sourceId);
    const connector = Connectors.findOne({ _id: sourceOps.getConnectorId(source), deleted: 'no' });
    if (isNil(connector)) throw new Error('The connection for this source has been deleted');
    return Sources.update(sourceId, { $set: { deleted: 'no' } });
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Deletes the source from database.
 *
 * @param {string} sourceId - The source id.
 * @returns {string} Return the id of the deleted source.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const hardDelete = sourceId => {
  check(sourceId, String);

  try {
    checkAuthentication();
    const source = Sources.findOne(sourceId);
    const CHECK_WRAPPER = getLoadParametersFromWrapper(source);
    if (checkIfExistingWrapper(source)) {
      const oldSource = Sources.findOne(sourceId);
      const loadParameters = {
        ...CHECK_WRAPPER,
        table_view_name: `${snakeCase(sourceOps.getName(oldSource)) || uuid()}_${sourceId}`
      };
      const response = HTTP.post(DELETE_SOURCE_API, {
        headers: { 'Content-Type': 'application/json' },
        data: { input_layer: { ...loadParameters }, user: Meteor.userId() }
      });
      const result = isHTTPResultOK(response);
      if (result.status === STATUS_ERROR) throw new Error(response.message);
    }

    return Sources.remove(sourceId);
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Checks if the source exists.
 *
 * @async
 * @param {object} source - The source to check.
 * @returns {object} The status of the source.
 */
export const checkIfValid = async source => {
  try {
    const loadParameters = getLoadParametersFromSource(source);
    const response = HTTP.post(CHECK_SOURCE_API, {
      headers: { 'Content-Type': 'application/json' },
      data: { input_layer: { ...loadParameters }, user: 'TEST' }
    });
    return isHTTPResultOK(response);
  } catch (error) {
    handleMethodException(error);
  }
};

/**
 * Checks the preview of the sources.
 *
 * @async
 * @param {object} source - The source.
 * @returns {object} The source list as a table object.
 */
export const previewSource = async source => {
  try {
    const loadParameters = getLoadParametersFromSource(source);
    const response = HTTP.post(PREVIEW_SOURCE_API, {
      headers: { 'Content-Type': 'application/json' },
      data: {
        input_layer: { ...loadParameters },
        user: Meteor.userId(),
        params: {
          view: `${snakeCase(sourceOps.getName(source))}_${uuid()}`,
          select: join(sourceOps.getAttributes(source)),
          where: '',
          groupBy: '',
          having: '',
          orderBy: '',
          limit: ''
        },
        overwrite: true
      }
    });
    const result = isHTTPResultOK(response);
    if (result.status === STATUS_ERROR) throw new Error(result.message);
    if (isNil(result.status)) throw new Error(result.message);

    if (connectorOps.isOutput(source)) return [];

    const rawCSV = result.message;
    const rowArray = rawCSV.split('\n');
    const tableTitle = map(rowArray[0].split(','), title => title.trim());
    const fullBodyArray = filter(rowArray, (row, index) => !isEmpty(row) && index > 0);
    const tableBody = map(fullBodyArray, row => row.trim().split(','));
    return { tableBody, tableTitle };
  } catch (error) {
    handleMethodException(error);
  }
};

/**
 * Check sthe source layers and their attributes.
 *
 * @async
 * @param {object} source - The source.
 * @returns {object[]} The layers list with their attributes as array.
 */
export const listSourceAttributes = async source => {
  try {
    const loadParameters = getLoadParametersFromSource(source);
    const response = HTTP.post(LIST_SOURCE_ATTRIBUTES_API, {
      headers: { 'Content-Type': 'application/json' },
      data: { input_layer: { ...loadParameters }, user: Meteor.userId() }
    });
    const result = isHTTPResultOK(response);
    if (result.status === STATUS_ERROR) throw new Error(result.message);

    if (connectorOps.isOutput(source)) return [];

    const layers = get(result, 'message.layers', []);
    const layer = find(layers, ['name', source.layerName]) || {};
    return layer.attributes;
  } catch (error) {
    handleMethodException(error);
  }
};

export const InfoInfrastructure = event => {
  try {
    const response = HTTP.post(CHECK_URLS_SOURCE_API, {
      headers: { 'Content-Type': 'application/json' },
      data: { check_url: event, user: 'TEST' }
    });
    const result = isHTTPResultOK(response);
    const loadParameters = get(result, 'message', []);
    return loadParameters;
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Checks the connector sources.
 *
 * @async
 * @param {object} loadParameters - The LoadParaeters.
 * @returns {object} The source list as a table object.
 * @throws {Error} - Meteor Error if anything goes wrong.
 */
export const listSchemas = async loadParameters => {
  try {
    const response = HTTP.post(loadParameters.direction, {
      headers: { 'Content-Type': 'application/json' },
      data: { input_layer: { ...loadParameters }, user: Meteor.userId() }
    });
    const result = isHTTPResultOK(response);
    if (result.status === STATUS_ERROR) throw new Error(result.message);
    if (result.message === FILE_TOO_LARGE_ERROR) throw new Error(result.message);
    if (isString(result)) throw new Error(result);

    const rawCSV = result.message;
    const rowArray = rawCSV.split('\n');
    const fullBodyArray = filter(rowArray.slice(1), row => !isEmpty(row));
    const tableBody = map(fullBodyArray, row => row.trim().split(','));
    const schema = [...new Set(map(tableBody, item => item[0]))];
    return { schema, tableBody };
  } catch (error) {
    handleMethodException(error);
  }
};
